import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import './DiscountManagement.css';

const DiscountManagement = () => {
  const user = useSelector(state => state.AuthReducer?.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'useradmin';

  const [discounts, setDiscounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Form states
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('permanent'); // 'permanent' | 'promotionnel'
  const [percentage, setPercentage] = useState(10);
  const [location, setLocation] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState('');

  // Filter states
  const [filterLocation, setFilterLocation] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorised: token } };
  };

  useEffect(() => {
    fetchLocations();
    fetchDiscounts();
  }, []);

  const fetchLocations = async () => {
    try {
      const res = await axios.get('/api/Locations/getAllLocations', getAuthHeaders());
      const list = Array.isArray(res.data) ? res.data : (res.data.locations || []);
      setLocations(list);
    } catch (err) {
      console.error('Erreur chargement localisations:', err);
    }
  };

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/discounts', getAuthHeaders());
      if (res.data?.success) {
        setDiscounts(res.data.discounts || []);
      }
    } catch (err) {
      console.error('Erreur chargement remises:', err);
      setErrorMsg('Impossible de charger les remises.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setName('');
    setType('permanent');
    setPercentage(10);
    setLocation('');
    setIsActive(true);
    setStartDate('');
    setEndDate('');
    setDescription('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || percentage === '' || percentage === null) {
      alert('Veuillez renseigner le nom et le pourcentage de la remise.');
      return;
    }

    try {
      setSubmitting(true);
      setMessage('');
      setErrorMsg('');

      const payload = {
        name: name.trim(),
        type,
        percentage: Number(percentage),
        location: location || null,
        isActive,
        startDate: type === 'promotionnel' && startDate ? startDate : null,
        endDate: type === 'promotionnel' && endDate ? endDate : null,
        description: description.trim()
      };

      if (editId) {
        const res = await axios.put(`/api/discounts/${editId}`, payload, getAuthHeaders());
        if (res.data?.success) {
          setMessage('✅ Remise modifiée avec succès.');
          resetForm();
          fetchDiscounts();
        }
      } else {
        const res = await axios.post('/api/discounts', payload, getAuthHeaders());
        if (res.data?.success) {
          setMessage('✅ Nouvelle remise enregistrée avec succès.');
          resetForm();
          fetchDiscounts();
        }
      }
    } catch (err) {
      console.error('Erreur enregistrement remise:', err);
      setErrorMsg(err.response?.data?.message || 'Erreur lors de l\'enregistrement de la remise.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (d) => {
    setEditId(d._id);
    setName(d.name);
    setType(d.type || 'permanent');
    setPercentage(d.percentage);
    setLocation(d.location?._id || d.location || '');
    setIsActive(d.isActive !== undefined ? d.isActive : true);
    setStartDate(d.startDate ? new Date(d.startDate).toISOString().slice(0, 10) : '');
    setEndDate(d.endDate ? new Date(d.endDate).toISOString().slice(0, 10) : '');
    setDescription(d.description || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await axios.put(`/api/discounts/${id}/toggle`, {}, getAuthHeaders());
      if (res.data?.success) {
        fetchDiscounts();
      }
    } catch (err) {
      console.error('Erreur changement statut:', err);
      alert('Erreur lors du changement de statut.');
    }
  };

  const handleDelete = async (id, discountName) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer la remise "${discountName}" ?`)) {
      return;
    }

    try {
      const res = await axios.delete(`/api/discounts/${id}`, getAuthHeaders());
      if (res.data?.success) {
        fetchDiscounts();
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression.');
    }
  };

  // Filtrage local
  const filteredDiscounts = discounts.filter(d => {
    if (filterLocation) {
      const dLocId = d.location?._id || d.location;
      if (filterLocation === 'global' && dLocId) return false;
      if (filterLocation !== 'global' && (!dLocId || String(dLocId) !== String(filterLocation))) return false;
    }
    if (filterType && d.type !== filterType) return false;
    if (filterStatus) {
      if (filterStatus === 'active' && !d.isActive) return false;
      if (filterStatus === 'inactive' && d.isActive) return false;
    }
    return true;
  });

  const totalActive = discounts.filter(d => d.isActive).length;
  const totalPermanent = discounts.filter(d => d.type === 'permanent').length;
  const totalPromo = discounts.filter(d => d.type === 'promotionnel').length;

  return (
    <div className="disc-admin-container">
      {/* En-tête principal */}
      <header className="disc-admin-header-card">
        <div className="disc-title-box">
          <h2>🏷️ Gestion des Remises & Promotions</h2>
          <p className="disc-subtitle">
            Configurez les remises permanentes (clients fidèles) et ponctuelles (fêtes & promotions) applicables en caisse selon l'emplacement.
          </p>
        </div>
        <div className="disc-kpi-summary">
          <div className="disc-kpi-pill green">
            <span className="kpi-num">{totalActive}</span>
            <span className="kpi-txt">Actives</span>
          </div>
          <div className="disc-kpi-pill blue">
            <span className="kpi-num">{totalPermanent}</span>
            <span className="kpi-txt">Clients Fidèles</span>
          </div>
          <div className="disc-kpi-pill amber">
            <span className="kpi-num">{totalPromo}</span>
            <span className="kpi-txt">Promotions & Fêtes</span>
          </div>
        </div>
      </header>

      {message && <div className="alert alert-success fw-bold py-2 mb-3">{message}</div>}
      {errorMsg && <div className="alert alert-danger fw-bold py-2 mb-3">{errorMsg}</div>}

      <div className="row g-3">
        {/* Colonne 1 : Formulaire de Création / Modification */}
        <div className="col-lg-4">
          <div className="disc-card">
            <div className="disc-card-header">
              <span>{editId ? '✏️ Modifier la Remise' : '➕ Configurer une Remise'}</span>
              {editId && (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetForm}>
                  Annuler
                </button>
              )}
            </div>
            <div className="disc-card-body">
              <form onSubmit={handleSubmit}>
                {/* 1. Intitulé */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-dark">Nom de la Remise *</label>
                  <input
                    type="text"
                    className="form-control fw-semibold"
                    placeholder="Ex: Remise Fidélité 10%, Promo Aïd..."
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                {/* 2. Type de Remise */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-dark">Type de Remise *</label>
                  <div className="disc-type-switch">
                    <button
                      type="button"
                      className={`disc-switch-btn ${type === 'permanent' ? 'active-permanent' : ''}`}
                      onClick={() => setType('permanent')}
                    >
                      🌟 Client Fidèle (Permanente)
                    </button>
                    <button
                      type="button"
                      className={`disc-switch-btn ${type === 'promotionnel' ? 'active-promo' : ''}`}
                      onClick={() => setType('promotionnel')}
                    >
                      🎉 Promo / Fête (Ponctuelle)
                    </button>
                  </div>
                </div>

                {/* 3. Pourcentage de Réduction */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-dark">Pourcentage de Réduction (%) *</label>
                  <div className="input-group mb-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      className="form-control form-control-lg fw-bold text-success text-center"
                      value={percentage}
                      onChange={(e) => setPercentage(e.target.value)}
                      required
                    />
                    <span className="input-group-text fw-bold text-success fs-5">%</span>
                  </div>
                  {/* Boutons d'accès rapide au pourcentage */}
                  <div className="disc-quick-pct-grid">
                    {[5, 10, 15, 20, 25, 30, 50].map(val => (
                      <button
                        key={val}
                        type="button"
                        className={`disc-quick-pct-btn ${Number(percentage) === val ? 'selected' : ''}`}
                        onClick={() => setPercentage(val)}
                      >
                        {val}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* 4. Localisation Assignée */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-dark">Localisation / Caisse POS</label>
                  <select
                    className="form-select fw-semibold"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  >
                    <option value="">🌍 Toutes les localisations (Globale)</option>
                    {locations.map(loc => (
                      <option key={loc._id} value={loc._id}>📍 {loc.name}</option>
                    ))}
                  </select>
                  <small className="text-muted d-block mt-1">
                    Laissez "Globale" pour rendre la remise disponible sur toutes les caisses du réseau.
                  </small>
                </div>

                {/* 5. Dates de validité (si promotionnel) */}
                {type === 'promotionnel' && (
                  <div className="mb-3 disc-date-box">
                    <div className="fw-bold small text-dark mb-2">📅 Période de Validité (Optionnelle)</div>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="small text-muted mb-1">Date début</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="small text-muted mb-1">Date fin</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* 6. Description / Notes */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-dark">Notes / Conditions (Optionnel)</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    placeholder="Ex: Valable à partir de 50 DT d'achat..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  ></textarea>
                </div>

                {/* 7. Statut d'activation */}
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="discountActiveCheck"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <label className="form-check-label fw-bold small text-dark" htmlFor="discountActiveCheck">
                    Remise active et immédiatement utilisable en caisse
                  </label>
                </div>

                {/* Bouton d'action */}
                <button type="submit" className="disc-submit-btn" disabled={submitting}>
                  {submitting ? 'Enregistrement...' : editId ? '💾 Mettre à jour la Remise' : '➕ Enregistrer la Remise'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Colonne 2 : Tableau des Remises Configurées */}
        <div className="col-lg-8">
          {/* Barre de Filtres */}
          <div className="disc-filter-bar">
            <div className="row g-2 align-items-center">
              <div className="col-md-4">
                <select
                  className="form-select form-select-sm fw-semibold"
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                >
                  <option value="">-- Tous les emplacements --</option>
                  <option value="global">🌍 Remises Globales uniquement</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={loc._id}>📍 {loc.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <select
                  className="form-select form-select-sm fw-semibold"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">-- Tous les types --</option>
                  <option value="permanent">🌟 Client Fidèle</option>
                  <option value="promotionnel">🎉 Promo / Fête</option>
                </select>
              </div>

              <div className="col-md-3">
                <select
                  className="form-select form-select-sm fw-semibold"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">-- Tous les statuts --</option>
                  <option value="active">✅ Actives uniquement</option>
                  <option value="inactive">⏸️ Inactives</option>
                </select>
              </div>

              <div className="col-md-2 text-end">
                <button className="btn btn-sm btn-outline-success w-100 fw-bold" onClick={fetchDiscounts}>
                  🔄 ({filteredDiscounts.length})
                </button>
              </div>
            </div>
          </div>

          {/* Tableau */}
          <div className="disc-card">
            <div className="disc-card-header d-flex justify-content-between align-items-center">
              <span>📋 Remises Configurées ({filteredDiscounts.length})</span>
            </div>
            <div className="disc-card-body p-0">
              {loading ? (
                <div className="text-center p-4 text-muted">
                  <div className="spinner-border spinner-border-sm text-success me-2"></div>
                  Chargement des remises...
                </div>
              ) : filteredDiscounts.length === 0 ? (
                <div className="text-center p-5 text-muted">
                  <p className="fs-1 mb-2">🏷️</p>
                  <h6 className="fw-bold">Aucune remise configurée</h6>
                  <p className="small">Utilisez le formulaire à gauche pour créer une première remise.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="disc-table">
                    <thead>
                      <tr>
                        <th>Nom de la Remise</th>
                        <th>Type</th>
                        <th>Taux (%)</th>
                        <th>Localisation</th>
                        <th>Validité</th>
                        <th>Statut</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDiscounts.map(d => (
                        <tr key={d._id} className={!d.isActive ? 'row-inactive' : ''}>
                          <td>
                            <strong className="d-block text-dark">{d.name}</strong>
                            {d.description && <span className="small text-muted">{d.description}</span>}
                          </td>
                          <td>
                            {d.type === 'permanent' ? (
                              <span className="disc-badge-type permanent">
                                🌟 Client Fidèle
                              </span>
                            ) : (
                              <span className="disc-badge-type promo">
                                🎉 Promo / Fête
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="disc-pct-badge">
                              {d.percentage}%
                            </span>
                          </td>
                          <td>
                            {d.location ? (
                              <span className="badge bg-light text-dark border">
                                📍 {d.location.name}
                              </span>
                            ) : (
                              <span className="badge bg-success-subtle text-success border border-success">
                                🌍 Globale (Toutes)
                              </span>
                            )}
                          </td>
                          <td>
                            {d.type === 'permanent' ? (
                              <span className="text-success small fw-bold">Illimitée</span>
                            ) : (
                              <span className="text-muted small">
                                {d.startDate ? new Date(d.startDate).toLocaleDateString('fr-FR') : 'Début'} →{' '}
                                {d.endDate ? new Date(d.endDate).toLocaleDateString('fr-FR') : 'Fin'}
                              </span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`disc-btn-status ${d.isActive ? 'active' : 'inactive'}`}
                              onClick={() => handleToggleStatus(d._id)}
                              title="Cliquer pour activer/désactiver"
                            >
                              {d.isActive ? '✅ Active' : '⏸️ Inactive'}
                            </button>
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="disc-btn-edit"
                              onClick={() => handleEdit(d)}
                              title="Modifier cette remise"
                            >
                              ✏️ Modifier
                            </button>
                            <button
                              type="button"
                              className="disc-btn-delete"
                              onClick={() => handleDelete(d._id, d.name)}
                              title="Supprimer définitivement"
                            >
                              🗑️ Supprimer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountManagement;
